import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import Sidebar from "./AdminPanel/Sidebar";
import TopBar from "./AdminPanel/TopBar";
import Persons from "./AdminPerson/Persons";
import { useDashboardTheme } from "../../context/DashboardThemeContext";
import { useAuth } from "../../context/AuthContext";
import { useAlert } from "../../context/AlertContext";

const API_BASE = "/api/persons";

function getToken() {
  return localStorage.getItem("accessToken") || localStorage.getItem("access") || "";
}

function userCan(user, code) {
  if (!user) return false;
  if (user.role === "super_admin" || user.role === "admin") return true;
  return (user.permissions || []).includes(code);
}

function getPersonRoles(person) {
  return [
    person.es_cliente ? "Cliente" : null,
    person.es_proveedor ? "Proveedor" : null,
    person.es_empleado ? "Empleado" : null,
    person.es_extranjero ? "Extranjero" : null,
  ]
    .filter(Boolean)
    .join(" / ") || "-";
}

export default function AdminPerson() {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const { dashboardTheme } = useDashboardTheme();
  const { user } = useAuth();
  const { showError, showSuccess } = useAlert();
  const isMobile = useMediaQuery("(max-width:900px)");

  const [persons, setPersons] = useState([]);
  const [search, setSearch] = useState("");
  const [rolFilter, setRolFilter] = useState("");
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);

  const [personModal, setPersonModal] = useState({
    open: false,
    person: null,
  });

  const [deleteModal, setDeleteModal] = useState({
    open: false,
    person: null,
  });

  const rowsPerPage = isMobile ? 10 : 30;
  const token = getToken();

  const canView = userCan(user, "persons.view");
  const canCreate = userCan(user, "persons.create");
  const canEdit = userCan(user, "persons.edit");
  const canDelete = userCan(user, "persons.delete");

  const visiblePersons = useMemo(() => {
    const start = page * rowsPerPage;
    return persons.slice(start, start + rowsPerPage);
  }, [persons, page, rowsPerPage]);

  const loadPersons = async () => {
    if (!canView) return;

    try {
      setLoading(true);

      const params = new URLSearchParams();

      if (search.trim()) {
        params.append("q", search.trim());
      }

      if (rolFilter) {
        params.append("rol", rolFilter);
      }

      const response = await fetch(`${API_BASE}/?${params.toString()}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        showError(data.error || data.detail || "No se pudieron cargar las personas");
        return;
      }

      setPersons(data.results || []);
    } catch {
      showError("Error de conexión");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const delay = setTimeout(() => {
      setPage(0);
      loadPersons();
    }, 350);

    return () => clearTimeout(delay);
  }, [search, rolFilter, canView]);

  useEffect(() => {
    setPage(0);
  }, [rowsPerPage]);

  const handleChangePage = (_event, newPage) => {
    setPage(newPage);
  };

  const openCreateModal = () => {
    setPersonModal({
      open: true,
      person: null,
    });
  };

  const openEditModal = (person) => {
    setPersonModal({
      open: true,
      person,
    });
  };

  const closePersonModal = () => {
    setPersonModal({
      open: false,
      person: null,
    });
  };

  const handlePersonSaved = () => {
    closePersonModal();
    loadPersons();
  };

  const openDeleteModal = (person) => {
    setDeleteModal({
      open: true,
      person,
    });
  };

  const closeDeleteModal = () => {
    setDeleteModal({
      open: false,
      person: null,
    });
  };

  const deletePerson = async () => {
    if (!deleteModal.person) return;

    try {
      const response = await fetch(`${API_BASE}/${deleteModal.person.id}/eliminar/`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        showError(data.error || data.detail || "No se pudo eliminar la persona");
        return;
      }

      showSuccess(data.mensaje || "Persona eliminada correctamente");
      closeDeleteModal();
      loadPersons();
    } catch {
      showError("Error de conexión");
    }
  };

  const outlinedButtonSx = {
    minHeight: 40,
    px: 2.4,
    borderRadius: "12px",
    textTransform: "none",
    fontWeight: 700,
    color: dashboardTheme.button_color,
    borderColor: dashboardTheme.button_color,
    boxShadow: "none",
    "&:hover": {
      borderColor: dashboardTheme.button_color,
      bgcolor: `${dashboardTheme.button_color}12`,
      boxShadow: "none",
    },
  };

  const smallButtonSx = {
    minHeight: 34,
    px: 1.4,
    borderRadius: "10px",
    textTransform: "none",
    fontWeight: 700,
    boxShadow: "none",
  };

  const editButtonSx = {
    ...smallButtonSx,
    color: dashboardTheme.button_color,
    borderColor: dashboardTheme.button_color,
    "&:hover": {
      borderColor: dashboardTheme.button_color,
      bgcolor: `${dashboardTheme.button_color}12`,
      boxShadow: "none",
    },
  };

  const deleteButtonSx = {
    ...smallButtonSx,
    color: "#dc2626",
    borderColor: "#dc2626",
    "&:hover": {
      borderColor: "#dc2626",
      bgcolor: "rgba(220,38,38,0.08)",
      boxShadow: "none",
    },
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: dashboardTheme.dashboard_background,
        display: "flex",
        color: dashboardTheme.text_color,
        overflowX: "hidden",
      }}
    >
      <Sidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <TopBar
          sidebarOpen={sidebarOpen}
          onToggleSidebar={() => setSidebarOpen((prev) => !prev)}
        />

        <Box
          component="main"
          sx={{
            flex: 1,
            width: "100%",
            p: { xs: 1.5, sm: 2, md: 3 },
          }}
        >
          <Box
            sx={{
              width: { xs: "100%", lg: "95%" },
              mx: "auto",
              mt: { xs: 1, md: 1.5 },
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: { xs: "stretch", sm: "center" },
                justifyContent: "space-between",
                flexDirection: { xs: "column", sm: "row" },
                gap: 1.5,
                mb: { xs: 1.5, md: 2 },
              }}
            >
              <Typography
                sx={{
                  fontSize: { xs: 24, sm: 26, md: 30 },
                  fontWeight: 600,
                  color: dashboardTheme.primary_color,
                  lineHeight: 1.2,
                }}
              >
                Personas
              </Typography>

              {canCreate && (
                <Button
                  variant="contained"
                  startIcon={<AddRoundedIcon />}
                  onClick={openCreateModal}
                  sx={dashboardTheme.buttonStyles.contained}
                >
                  Añadir nueva persona
                </Button>
              )}
            </Box>

            <Paper elevation={0} sx={dashboardTheme.tableStyles.wrapper}>
              <Box
                sx={{
                  ...dashboardTheme.tableStyles.toolbar,
                  display: "flex",
                  flexDirection: { xs: "column", md: "row" },
                  gap: 1.5,
                }}
              >
                <TextField
                  fullWidth
                  size="small"
                  label="Buscar por razón social, nombre, identificación o teléfono"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  sx={{
                    maxWidth: { xs: "100%", md: 460 },
                    ...dashboardTheme.inputStyles,
                  }}
                />

                <FormControl
                  size="small"
                  sx={{
                    minWidth: { xs: "100%", md: 190 },
                    ...dashboardTheme.inputStyles,
                  }}
                >
                  <InputLabel>Rol</InputLabel>
                  <Select
                    label="Rol"
                    value={rolFilter}
                    onChange={(event) => setRolFilter(event.target.value)}
                  >
                    <MenuItem value="">Todos</MenuItem>
                    <MenuItem value="cliente">Cliente</MenuItem>
                    <MenuItem value="proveedor">Proveedor</MenuItem>
                    <MenuItem value="empleado">Empleado</MenuItem>
                    <MenuItem value="extranjero">Extranjero</MenuItem>
                  </Select>
                </FormControl>
              </Box>

              <Box sx={dashboardTheme.tableStyles.tableArea}>
                <TableContainer sx={dashboardTheme.tableStyles.container}>
                  <Table size="small" sx={dashboardTheme.tableStyles.table}>
                    <TableHead>
                      <TableRow sx={dashboardTheme.tableStyles.headRow}>
                        <TableCell
                          sx={{
                            ...dashboardTheme.tableStyles.headCell,
                            minWidth: 260,
                          }}
                        >
                          Razón Social / Nombre
                        </TableCell>

                        <TableCell
                          sx={{
                            ...dashboardTheme.tableStyles.headCell,
                            minWidth: 190,
                          }}
                        >
                          Identificación
                        </TableCell>

                        <TableCell
                          sx={{
                            ...dashboardTheme.tableStyles.headCell,
                            minWidth: 220,
                          }}
                        >
                          Rol
                        </TableCell>

                        <TableCell sx={dashboardTheme.tableStyles.headCell}>
                          Teléfono
                        </TableCell>

                        <TableCell align="right" sx={dashboardTheme.tableStyles.headCell}>
                          Acciones
                        </TableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {visiblePersons.map((person) => (
                        <TableRow
                          key={person.id}
                          hover
                          sx={dashboardTheme.tableStyles.bodyRow}
                        >
                          <TableCell>
                            <Typography
                              sx={{
                                fontSize: 14,
                                fontWeight: 700,
                                color: dashboardTheme.text_color,
                                lineHeight: 1.25,
                              }}
                            >
                              {person.razon_social || "-"}
                            </Typography>

                            {person.nombre_comercial && (
                              <Typography
                                sx={{
                                  mt: 0.35,
                                  fontSize: 12.5,
                                  fontWeight: 500,
                                  color:
                                    dashboardTheme.theme_mode === "dark"
                                      ? "rgba(255,255,255,0.62)"
                                      : "#4b5563",
                                  lineHeight: 1.25,
                                }}
                              >
                                {person.nombre_comercial}
                              </Typography>
                            )}
                          </TableCell>

                          <TableCell sx={{ whiteSpace: "nowrap" }}>
                            {person.cedula || person.ruc ? (
                              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.35 }}>
                                {person.cedula && (
                                  <Typography
                                    sx={{
                                      fontSize: 13,
                                      fontWeight: 500,
                                      color: dashboardTheme.text_color,
                                      lineHeight: 1.25,
                                    }}
                                  >
                                    Cédula: {person.cedula}
                                  </Typography>
                                )}

                                {person.ruc && (
                                  <Typography
                                    sx={{
                                      fontSize: 13,
                                      fontWeight: 500,
                                      color: dashboardTheme.text_color,
                                      lineHeight: 1.25,
                                    }}
                                  >
                                    RUC: {person.ruc}
                                  </Typography>
                                )}
                              </Box>
                            ) : (
                              "-"
                            )}
                          </TableCell>

                          <TableCell sx={{ whiteSpace: "nowrap" }}>
                            {getPersonRoles(person)}
                          </TableCell>

                          <TableCell sx={{ whiteSpace: "nowrap" }}>
                            {person.telefono || "-"}
                          </TableCell>

                          <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "flex-end",
                                gap: 1,
                              }}
                            >
                              {canEdit && (
                                <Button
                                  variant="outlined"
                                  size="small"
                                  startIcon={<EditRoundedIcon />}
                                  onClick={() => openEditModal(person)}
                                  sx={editButtonSx}
                                >
                                  Editar
                                </Button>
                              )}

                              {canDelete && (
                                <Button
                                  variant="outlined"
                                  size="small"
                                  startIcon={<DeleteOutlineRoundedIcon />}
                                  onClick={() => openDeleteModal(person)}
                                  sx={deleteButtonSx}
                                >
                                  Eliminar
                                </Button>
                              )}
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}

                      {!loading && visiblePersons.length === 0 && canView && (
                        <TableRow>
                          <TableCell
                            colSpan={5}
                            align="center"
                            sx={dashboardTheme.tableStyles.emptyCell}
                          >
                            No hay personas registradas
                          </TableCell>
                        </TableRow>
                      )}

                      {loading && (
                        <TableRow>
                          <TableCell
                            colSpan={5}
                            align="center"
                            sx={dashboardTheme.tableStyles.emptyCell}
                          >
                            Cargando personas...
                          </TableCell>
                        </TableRow>
                      )}

                      {!canView && (
                        <TableRow>
                          <TableCell
                            colSpan={5}
                            align="center"
                            sx={dashboardTheme.tableStyles.emptyCell}
                          >
                            No tienes permisos para ver personas
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>

              <TablePagination
                component="div"
                count={persons.length}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                rowsPerPageOptions={[]}
                labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
                sx={dashboardTheme.tableStyles.pagination}
              />
            </Paper>
          </Box>
        </Box>
      </Box>

      <Persons
        open={personModal.open}
        person={personModal.person}
        onClose={closePersonModal}
        onSaved={handlePersonSaved}
      />

      <Dialog
        open={deleteModal.open}
        onClose={closeDeleteModal}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: {
            borderRadius: "16px",
            bgcolor: dashboardTheme.card_background,
            border: `1px solid ${dashboardTheme.border_color}`,
            boxShadow: "none",
          },
        }}
      >
        <DialogTitle
          sx={{
            color: dashboardTheme.text_color,
            fontSize: 20,
            fontWeight: 600,
          }}
        >
          Eliminar persona
        </DialogTitle>

        <DialogContent>
          <Typography
            sx={{
              color: dashboardTheme.text_color,
              fontSize: 15,
            }}
          >
            ¿Seguro que deseas eliminar {deleteModal.person?.razon_social || "esta persona"}?
          </Typography>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            variant="outlined"
            onClick={closeDeleteModal}
            sx={outlinedButtonSx}
          >
            Cancelar
          </Button>

          <Button
            variant="outlined"
            onClick={deletePerson}
            sx={deleteButtonSx}
          >
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}